import {
  lazy,
  Suspense,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import './App.css'
import { trackEvent, trackEventOnce, trackPageView, trackWaitlistConversion } from './analytics'
import i18n, {
  currentLocale,
  isLocale,
  localeFromPath,
  localizedPath,
  pathWithoutLocale,
  type Locale,
} from './i18n'
import LegalPage from './LegalPage'
import ArticlesPage from './articles/ArticlesPage'
import PublicFooter from './PublicFooter'
import PublicHeader from './PublicHeader'
import { syncDocumentSeo } from './seo'

type FormState = 'idle' | 'submitting' | 'success' | 'error'
type WaitlistJoinedDetail = { email: string; message: string; agents: string[] }
type Severity = 'critical' | 'warning' | 'healthy'
type DescriptionMode = 'detailed' | 'short' | 'simple'

const DesignSystem = lazy(() => import('./DesignSystem'))
const DesignApp = lazy(() => import('./DesignApp'))
const ProductApp = lazy(() => import('./ProductApp'))
const ArticlePage = lazy(() => import('./articles/ArticlePage'))
const DocsPage = lazy(() => import('./docs/DocsPage'))

type AgentIssue = {
  id: string
  severity: Exclude<Severity, 'healthy'>
  title: string
  summary: string
  impact: string
  category: string
  agent: string
  detected: string
  confidence: number
  evidence: string[]
  fix: string
  fixSteps: string[]
  boundary: string
  technical: {
    check: string
    source: string
    observation: string
  }
}

const waitlistEndpoint = import.meta.env.VITE_WAITLIST_API_URL
  || (import.meta.env.PROD ? 'https://api.sidewisp.com/api/waitlist' : '/api/waitlist')
const waitlistJoinedEvent = 'sidewisp:waitlist-joined'
const integrationOptions = ['OpenClaw', 'Claude Code', 'Codex', 'Hermes', 'Other'] as const
const builtForOptions = ['OpenClaw', 'Claude Code', 'Codex', 'Hermes'] as const
type IntegrationName = (typeof integrationOptions)[number]

function getIssues(t: TFunction): AgentIssue[] {
  return [
  {
    id: 'memory',
    severity: 'critical',
    title: t('issues.memory.title'),
    summary: t('issues.memory.summary'),
    impact: t('issues.memory.impact'),
    category: t('issues.memory.category'),
    agent: 'OpenClaw / Agent Jarvis',
    detected: t('issues.memory.detected'),
    confidence: 96,
    evidence: t('issues.memory.evidence', { returnObjects: true }) as string[],
    fix: t('issues.memory.fix'),
    fixSteps: t('issues.memory.fixSteps', { returnObjects: true }) as string[],
    boundary: t('issues.memory.boundary'),
    technical: {
      check: 'memory.write_activity',
      source: t('issues.memory.source'),
      observation: 'completed_runs=8 · durable_writes=0 · path=reachable',
    },
  },
  {
    id: 'loop',
    severity: 'critical',
    title: t('issues.loop.title'),
    summary: t('issues.loop.summary'),
    impact: t('issues.loop.impact'),
    category: t('issues.loop.category'),
    agent: 'Codex / Builder',
    detected: t('issues.loop.detected'),
    confidence: 99,
    evidence: t('issues.loop.evidence', { returnObjects: true }) as string[],
    fix: t('issues.loop.fix'),
    fixSteps: t('issues.loop.fixSteps', { returnObjects: true }) as string[],
    boundary: t('issues.loop.boundary'),
    technical: {
      check: 'execution.repeat_signature',
      source: t('issues.loop.source'),
      observation: 'matching_calls=12 · output_delta=0 · window=9m',
    },
  },
  {
    id: 'credential',
    severity: 'warning',
    title: t('issues.credential.title'),
    summary: t('issues.credential.summary'),
    impact: t('issues.credential.impact'),
    category: t('issues.credential.category'),
    agent: 'Claude Code / Review',
    detected: t('issues.credential.detected'),
    confidence: 100,
    evidence: t('issues.credential.evidence', { returnObjects: true }) as string[],
    fix: t('issues.credential.fix'),
    fixSteps: t('issues.credential.fixSteps', { returnObjects: true }) as string[],
    boundary: t('issues.credential.boundary'),
    technical: {
      check: 'tools.credential_validity',
      source: t('issues.credential.source'),
      observation: 'provider=github · status=401 · local_repo=healthy',
    },
  },
  {
    id: 'schedule',
    severity: 'warning',
    title: t('issues.schedule.title'),
    summary: t('issues.schedule.summary'),
    impact: t('issues.schedule.impact'),
    category: t('issues.schedule.category'),
    agent: 'OpenClaw / Auditor',
    detected: t('issues.schedule.detected'),
    confidence: 91,
    evidence: t('issues.schedule.evidence', { returnObjects: true }) as string[],
    fix: t('issues.schedule.fix'),
    fixSteps: t('issues.schedule.fixSteps', { returnObjects: true }) as string[],
    boundary: t('issues.schedule.boundary'),
    technical: {
      check: 'availability.expected_run',
      source: t('issues.schedule.source'),
      observation: 'lateness=47m · active_session=none · host=healthy',
    },
  },
  ]
}

function getHealthChecks(t: TFunction) {
  return (['availability', 'execution', 'memory', 'tools', 'outcome', 'cost'] as const).map((key, index) => ({
    index: String(index + 1).padStart(2, '0'),
    title: t(`checks.items.${key}.title`),
    description: t(`checks.items.${key}.description`),
  }))
}

function getProductFlow(t: TFunction) {
  return [
  {
    index: '01',
    title: t('flow.steps.detect.title'),
    label: t('flow.steps.detect.label'),
    description: t('flow.steps.detect.description'),
    metric: '12×',
    metricLabel: t('flow.steps.detect.metricLabel'),
    detail: t('flow.steps.detect.detail'),
    state: 'critical',
  },
  {
    index: '02',
    title: t('flow.steps.explain.title'),
    label: t('flow.steps.explain.label'),
    description: t('flow.steps.explain.description'),
    metric: '99%',
    metricLabel: t('flow.steps.explain.metricLabel'),
    detail: t('flow.steps.explain.detail'),
    state: 'evidence',
  },
  {
    index: '03',
    title: t('flow.steps.approve.title'),
    label: t('flow.steps.approve.label'),
    description: t('flow.steps.approve.description'),
    metric: '1',
    metricLabel: t('flow.steps.approve.metricLabel'),
    detail: t('flow.steps.approve.detail'),
    state: 'approval',
  },
  {
    index: '04',
    title: t('flow.steps.verify.title'),
    label: t('flow.steps.verify.label'),
    description: t('flow.steps.verify.description'),
    metric: t('flow.steps.verify.metric'),
    metricLabel: t('flow.steps.verify.metricLabel'),
    detail: t('flow.steps.verify.detail'),
    state: 'healthy',
  },
  ]
}

function getRecoveryStates(t: TFunction) {
  return [
    { key: 'working', name: t('mobile.recovery.states.working.name'), status: 'healthy' as Severity, detail: t('mobile.recovery.states.working.detail'), action: t('mobile.recovery.states.working.action') },
    { key: 'waiting', name: t('mobile.recovery.states.waiting.name'), status: 'warning' as Severity, detail: t('mobile.recovery.states.waiting.detail'), action: t('mobile.recovery.states.waiting.action') },
    { key: 'stuck', name: t('mobile.recovery.states.stuck.name'), status: 'critical' as Severity, detail: t('mobile.recovery.states.stuck.detail'), action: t('mobile.recovery.states.stuck.action') },
    { key: 'uncertain', name: t('mobile.recovery.states.uncertain.name'), status: 'warning' as Severity, detail: t('mobile.recovery.states.uncertain.detail'), action: t('mobile.recovery.states.uncertain.action') },
  ]
}

function getProjectDescriptions(t: TFunction): Array<{
  mode: DescriptionMode
  index: string
  label: string
  hint: string
  copy: string
}> {
  return [
  {
    mode: 'simple',
    index: '01',
    label: t('descriptions.simple.label'),
    hint: t('descriptions.simple.hint'),
    copy: t('descriptions.simple.copy'),
  },
  {
    mode: 'short',
    index: '02',
    label: t('descriptions.short.label'),
    hint: t('descriptions.short.hint'),
    copy: t('descriptions.short.copy'),
  },
  {
    mode: 'detailed',
    index: '03',
    label: t('descriptions.detailed.label'),
    hint: t('descriptions.detailed.hint'),
    copy: t('descriptions.detailed.copy'),
  },
  ]
}

function ArrowIcon() {
  return (
    <svg className="arrow-icon" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M3.75 9h10.5M10 4.75 14.25 9 10 13.25" />
    </svg>
  )
}

function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`brand-mark${compact ? ' brand-mark--compact' : ''}`} aria-hidden="true" />
  )
}

function IntegrationIcon({ name }: { name: IntegrationName }) {
  if (name === 'OpenClaw') {
    return (
      <svg className="integration-logo integration-logo--openclaw" viewBox="0 0 120 120" aria-hidden="true">
        <path d="M60 10 C30 10 15 35 15 55 C15 75 30 95 45 100 L45 110 L55 110 L55 100 C55 100 60 102 65 100 L65 110 L75 110 L75 100 C90 95 105 75 105 55 C105 35 90 10 60 10Z" fill="currentColor" />
        <path d="M20 45 C5 40 0 50 5 60 C10 70 20 65 25 55 C28 48 25 45 20 45Z" fill="currentColor" />
        <path d="M100 45 C115 40 120 50 115 60 C110 70 100 65 95 55 C92 48 95 45 100 45Z" fill="currentColor" />
        <path d="M45 15 Q35 5 30 8M75 15 Q85 5 90 8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        <circle cx="45" cy="35" r="6" fill="#050810" />
        <circle cx="75" cy="35" r="6" fill="#050810" />
        <circle cx="46" cy="34" r="2.5" fill="#00e5cc" />
        <circle cx="76" cy="34" r="2.5" fill="#00e5cc" />
      </svg>
    )
  }

  if (name === 'Claude Code') {
    return (
      <svg className="integration-logo integration-logo--claude" viewBox="0 0 100 100" aria-hidden="true">
        <path d="M25.7146 63.2153L41.4393 54.3917L41.7025 53.6226L41.4393 53.1976H40.6705L38.0394 53.0359L29.054 52.7929L21.2624 52.4691L13.7134 52.0644L11.8111 51.6594L10.0303 49.3118L10.2123 48.138L11.8111 47.0657L14.0981 47.2681L19.1574 47.6119L26.7467 48.138L32.2516 48.4618L40.4073 49.3118H41.7025L41.8846 48.7857L41.4393 48.4618L41.0955 48.138L33.243 42.8155L24.7432 37.1894L20.2909 33.9513L17.8824 32.3119L16.6684 30.774L16.1422 27.4147L18.328 25.0062L21.2624 25.2088L22.0112 25.4112L24.9861 27.6979L31.3407 32.616L39.6381 38.7273L40.8525 39.7391L41.3381 39.395L41.399 39.1523L40.8525 38.2415L36.3394 30.0858L31.5227 21.7883L29.3775 18.3478L28.811 16.2837C28.6087 15.4334 28.4669 14.7252 28.4669 13.8549L30.9563 10.4753L32.3321 10.0303L35.6515 10.4756L37.0479 11.6897L39.112 16.4052L42.4513 23.8327L47.6321 33.9313L49.15 36.9265L49.9594 39.6991L50.2632 40.5491H50.7894V40.0632L51.2141 34.3766L52.0035 27.3944L52.7726 18.4087L53.0358 15.8793L54.2905 12.8435L56.7795 11.2041L58.7224 12.135L60.3212 14.422L60.0986 15.899L59.1474 22.0718L57.2857 31.7458L56.0713 38.2218H56.7795L57.5892 37.4121L60.8677 33.061L66.3723 26.18L68.801 23.448L71.6342 20.4325L73.4556 18.9957H76.8962L79.4255 22.7601L78.2926 26.6456L74.7509 31.1384L71.8163 34.943L67.607 40.6097L64.9758 45.1431L65.2188 45.5072L65.8464 45.4466L75.358 43.4228L80.4984 42.4917L86.6304 41.4393L89.4033 42.7346L89.7065 44.0502L88.6135 46.7419L82.0566 48.3607L74.3662 49.8989L62.9118 52.6109L62.77 52.7121L62.9321 52.9144L68.0925 53.4L70.2987 53.5214H75.7021L85.7601 54.2702L88.3912 56.0108L89.9697 58.1358L89.7065 59.7545L85.6589 61.8189L80.1949 60.5236L67.4452 57.4881L63.0735 56.3952H62.4665V56.7596L66.1093 60.3213L72.7877 66.3523L81.1461 74.1236L81.5707 76.0462L80.4984 77.5638L79.3649 77.4021L72.0186 71.8772L69.1854 69.3879L62.77 63.9844H62.3453V64.5509L63.8223 66.7164L71.6342 78.4544L72.0389 82.0567L71.4725 83.2308L69.4487 83.939L67.2222 83.534L62.6485 77.1189L57.9333 69.8937L54.1284 63.4177L53.6631 63.6809L51.4167 87.8651L50.3644 89.0995L47.9356 90.0303L45.9121 88.4924L44.8392 86.0031L45.9118 81.0852L47.2071 74.6701L48.2594 69.5699L49.2106 63.2356L49.7773 61.131L49.7367 60.9892L49.2715 61.0498L44.4954 67.607L37.23 77.4224L31.4825 83.5746L30.1063 84.1211L27.7181 82.8864L27.9408 80.6805L29.2763 78.7177L37.2297 68.5988L42.026 62.3248L45.1227 58.7025L45.1024 58.176H44.9204L23.7917 71.8975L20.0274 72.3831L18.4083 70.8655L18.6106 68.3761L19.3798 67.5664L25.7343 63.195L25.7146 63.2153Z" fill="currentColor" />
      </svg>
    )
  }

  if (name === 'Codex') {
    return (
      <svg className="integration-logo integration-logo--codex" viewBox="0 0 100 100" aria-hidden="true">
        <path d="M83.7733 42.8087C84.6678 40.1149 84.9771 37.2613 84.6807 34.4385C84.3843 31.6156 83.489 28.8885 82.0544 26.4394C77.6908 18.8436 68.9203 14.9365 60.3548 16.7725C57.9831 14.1344 54.9591 12.1668 51.5864 11.0673C48.2137 9.96772 44.611 9.77498 41.1402 10.5084C37.6694 11.2418 34.4527 12.8755 31.8132 15.2455C29.1736 17.6155 27.204 20.6383 26.1024 24.0103C23.3212 24.5806 20.6938 25.738 18.3958 27.405C16.0977 29.0721 14.1819 31.2104 12.7765 33.6772C8.36538 41.2609 9.3669 50.8267 15.2527 57.3327C14.3549 60.0251 14.0424 62.8782 14.3361 65.7012C14.6298 68.5241 15.523 71.2518 16.9558 73.7017C21.325 81.3002 30.1011 85.207 38.6712 83.3686C40.5554 85.4904 42.8707 87.1858 45.4623 88.3416C48.0539 89.4975 50.8622 90.0871 53.6999 90.0713C62.4793 90.079 70.2575 84.4114 72.9393 76.0515C75.7201 75.4802 78.347 74.3225 80.6449 72.6555C82.9427 70.9886 84.8587 68.8507 86.2649 66.3846C90.6227 58.8145 89.6172 49.3005 83.7733 42.8087ZM53.6999 84.8356C50.1955 84.8411 46.801 83.6129 44.1116 81.3661L44.5848 81.098L60.5123 71.9043C60.9087 71.6718 61.2379 71.3402 61.4674 70.942C61.6969 70.5439 61.8189 70.0929 61.8215 69.6333V47.1769L68.5553 51.072C68.6225 51.1063 68.6694 51.1707 68.6814 51.2456V69.854C68.6641 78.1208 61.9667 84.8183 53.6999 84.8356ZM21.4977 71.0843C19.7402 68.0497 19.1092 64.4925 19.7156 61.0386L20.1885 61.3225L36.1321 70.5165C36.5266 70.748 36.9757 70.87 37.4331 70.87C37.8905 70.87 38.3396 70.748 38.7341 70.5165L58.21 59.2883V67.0628C58.2081 67.1031 58.1973 67.1424 58.1782 67.1779C58.1591 67.2134 58.1322 67.2441 58.0996 67.2678L41.9671 76.5722C34.798 80.7022 25.6388 78.2463 21.4977 71.0843ZM17.3026 36.3898C19.0723 33.3357 21.8655 31.0062 25.1878 29.8138V48.7376C25.1818 49.1949 25.2986 49.6453 25.5261 50.042C25.7535 50.4387 26.0833 50.7671 26.4809 50.9928L45.8622 62.1739L39.1283 66.069C39.0919 66.0883 39.0513 66.0984 39.0101 66.0984C38.9689 66.0984 38.9283 66.0883 38.8919 66.069L22.7908 56.7809C15.6359 52.6337 13.1822 43.4816 17.3026 36.3112V36.3898ZM72.624 49.2426L53.1792 37.9512L59.8976 34.0718C59.9341 34.0524 59.9747 34.0423 60.016 34.0423C60.0573 34.0423 60.0979 34.0524 60.1344 34.0718L76.2355 43.3761C78.6973 44.7966 80.7043 46.8882 82.0221 49.4065C83.3398 51.9249 83.914 54.7661 83.6775 57.5985C83.4411 60.431 82.4038 63.1377 80.6867 65.4027C78.9696 67.6677 76.6436 69.3975 73.9803 70.3901V51.466C73.9663 51.0096 73.834 50.5647 73.5962 50.1749C73.3584 49.7851 73.0234 49.4638 72.624 49.2426ZM79.3261 39.1657L78.8529 38.8815L62.9411 29.6089C62.5442 29.376 62.0924 29.2532 61.6322 29.2532C61.172 29.2532 60.7202 29.376 60.3233 29.6089L40.8629 40.8374V33.0628C40.8587 33.0233 40.8654 32.9834 40.882 32.9473C40.8987 32.9113 40.9248 32.8803 40.9575 32.8579L57.0586 23.5692C59.5263 22.1476 62.3478 21.458 65.193 21.5811C68.0382 21.7042 70.7896 22.6348 73.1253 24.2642C75.461 25.8936 77.2845 28.1543 78.3825 30.782C79.4806 33.4097 79.8077 36.2957 79.3257 39.1025V39.1657H79.3261ZM37.1888 52.9484L30.455 49.069C30.4213 49.0487 30.3925 49.0212 30.3707 48.9884C30.3488 48.9557 30.3345 48.9186 30.3286 48.8797V30.3188C30.3323 27.4714 31.1466 24.6839 32.6761 22.2822C34.2057 19.8805 36.3874 17.9639 38.9661 16.7564C41.5448 15.549 44.4139 15.1005 47.2381 15.4636C50.0622 15.8267 52.7247 16.9862 54.9141 18.8067L54.4409 19.0748L38.5134 28.2686C38.117 28.5011 37.7879 28.8327 37.5584 29.2308C37.329 29.629 37.207 30.0799 37.2045 30.5395L37.1888 52.9487V52.9484ZM40.8472 45.0632L49.5209 40.0643L58.21 45.0635V55.0615L49.5523 60.0608L40.8632 55.0615L40.8472 45.0632Z" fill="currentColor" />
      </svg>
    )
  }

  if (name === 'Hermes') {
    return <img className="integration-logo integration-logo--hermes" src="/brand/integrations/hermes-agent-64.webp" width="64" height="64" alt="Hermes Agent" aria-hidden="true" draggable={false} />
  }

  return (
    <svg className="integration-logo integration-logo--other" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

function Logo() {
  const { t } = useTranslation()
  return (
    <a className="logo" href="#top" aria-label={t('accessibility.backToTop')}>
      <BrandMark compact />
      <span>SIDEWISP</span>
    </a>
  )
}

function SeverityDot({ severity }: { severity: Severity }) {
  return <i className={`severity-dot severity-dot--${severity}`} aria-hidden="true" />
}

function MobileSeverityIcon({ severity }: { severity: Exclude<Severity, 'healthy'> }) {
  return <i className={`mobile-severity-icon mobile-severity-icon--${severity}`} aria-hidden="true"><span>!</span></i>
}

function WaitlistForm({ surface }: { surface: 'hero' | 'footer' }) {
  const { t } = useTranslation()
  const fieldId = useId()
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [formState, setFormState] = useState<FormState>('idle')
  const [message, setMessage] = useState('')
  const [agents, setAgents] = useState<string[]>([])
  const [attribution] = useState(() => {
    const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search)
    return {
      utmSource: params.get('utm_source') || '',
      utmMedium: params.get('utm_medium') || '',
      utmCampaign: params.get('utm_campaign') || '',
      utmContent: params.get('utm_content') || '',
      gclid: params.get('gclid') || '',
      wbraid: params.get('wbraid') || '',
      gbraid: params.get('gbraid') || '',
    }
  })

  useEffect(() => {
    const syncJoinedState = (event: Event) => {
      const detail = (event as CustomEvent<WaitlistJoinedDetail>).detail
      if (!detail?.email) return
      setEmail(detail.email)
      setAgents(detail.agents)
      setMessage(detail.message)
      setFormState('success')
    }
    window.addEventListener(waitlistJoinedEvent, syncJoinedState)
    return () => window.removeEventListener(waitlistJoinedEvent, syncJoinedState)
  }, [])

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const normalizedEmail = email.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setFormState('error')
      setMessage(t('waitlist.invalidEmail'))
      return
    }
    if (agents.length === 0) {
      setFormState('error')
      setMessage(t('waitlist.chooseRuntime'))
      return
    }
    setEmail(normalizedEmail)
    setFormState('submitting')
    setMessage('')
    trackEvent('sidewisp_waitlist_submit', {
      form_surface: surface,
      agent_types: agents,
      agent_count: agents.length,
    })

    try {
      const response = await fetch(waitlistEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'join', email: normalizedEmail, agents, website, source: surface, ...attribution }),
      })
      const result = (await response.json()) as { message?: string }
      if (!response.ok) throw new Error(result.message || t('waitlist.genericError'))
      const successMessage = t('waitlist.success')
      setFormState('success')
      setMessage(successMessage)
      trackEvent('sidewisp_waitlist_joined', {
        form_surface: surface,
        agent_types: agents,
        agent_count: agents.length,
      })
      trackWaitlistConversion()
      window.dispatchEvent(new CustomEvent<WaitlistJoinedDetail>(waitlistJoinedEvent, {
        detail: { email: normalizedEmail, message: successMessage, agents },
      }))
    } catch (error) {
      setFormState('error')
      setMessage(error instanceof Error ? error.message : t('waitlist.genericError'))
      trackEvent('sidewisp_waitlist_error', { form_surface: surface })
    }
  }

  function toggleAgent(agent: string) {
    setAgents((currentAgents) => {
      const selected = !currentAgents.includes(agent)
      if (selected) {
        trackEventOnce('sidewisp_agent_selected', {
          form_surface: surface,
          agent_type: agent,
        })
      }
      return selected
        ? [...currentAgents, agent]
        : currentAgents.filter((current) => current !== agent)
    })
  }

  function updateEmail(nextEmail: string) {
    setEmail(nextEmail)
    if (nextEmail.length > 0) {
      trackEventOnce('sidewisp_email_input_started', {
        form_surface: surface,
      })
    }
  }

  if (formState === 'success') {
    return (
      <div className="waitlist-success" role="status" aria-live="polite">
        <div className="waitlist-success__line">
          <span>✓</span>
          <div><strong>{message}</strong><small>{t('waitlist.successNote')}</small></div>
        </div>
        <div className="waitlist-success__agents">{t('waitlist.running', { agents: agents.join(', ') })}</div>
      </div>
    )
  }

  return (
    <div className="waitlist-wrap" aria-live="polite">
      <div className="waitlist-card">
        <div className="waitlist-card__head">
          <div><span>{t('waitlist.heading')}</span><strong>{t('waitlist.selectOne')}</strong></div>
          <p>{t('waitlist.preview')}</p>
        </div>
        <fieldset className="waitlist-profile">
          <legend><span>{t('waitlist.legend')}</span><small>{t('waitlist.selectAll')}</small></legend>
          <div className="runtime-grid">
            {integrationOptions.map((agent) => (
              <button
                type="button"
                key={agent}
                className={`runtime-option runtime-option--${agent.toLowerCase().replaceAll(' ', '-')}${agents.includes(agent) ? ' is-selected' : ''}`}
                aria-pressed={agents.includes(agent)}
                onClick={() => toggleAgent(agent)}
                disabled={formState === 'submitting'}
              >
                <span className="runtime-option__icon"><IntegrationIcon name={agent} /></span>
                <span className="runtime-option__name">{agent === 'Other' ? t('waitlist.other') : agent}</span>
                <i className="runtime-option__check" aria-hidden="true">✓</i>
              </button>
            ))}
          </div>
        </fieldset>
        <form className="waitlist-form" onSubmit={submit} noValidate>
          <div className="waitlist-form__field">
            <label className="waitlist-form__label" htmlFor={`${fieldId}-email`}>{t('waitlist.reachYou')}</label>
            <input
              id={`${fieldId}-email`}
              name="email"
              type="email"
              className="ym-disable-keys ym-hide-content"
              value={email}
              onChange={(event) => updateEmail(event.target.value)}
              placeholder={t('waitlist.emailPlaceholder')}
              autoComplete="email"
              inputMode="email"
              required
              disabled={formState === 'submitting'}
              aria-invalid={formState === 'error'}
              aria-describedby={`${fieldId}-note`}
            />
          </div>
          <div className="honeypot" aria-hidden="true">
            <label htmlFor={`${fieldId}-website`}>{t('waitlist.website')}</label>
            <input
              id={`${fieldId}-website`}
              name="website"
              type="text"
              className="ym-disable-keys ym-hide-content"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>
          <button type="submit" disabled={formState === 'submitting'} aria-busy={formState === 'submitting'}>
            <span>{formState === 'submitting' ? t('waitlist.joining') : t('waitlist.join')}</span>
            <ArrowIcon />
          </button>
        </form>
      </div>
      <div className="form-note-row" id={`${fieldId}-note`}>
        {formState === 'error' && <span className="form-error" role="alert">{message}</span>}
      </div>
    </div>
  )
}

function HealthRing({ score, small = false }: { score: number; small?: boolean }) {
  return (
    <div className={`health-ring${small ? ' health-ring--small' : ''}`}>
      <svg className="health-ring__chart" viewBox="0 0 100 100" aria-hidden="true">
        <circle className="health-ring__track" cx="50" cy="50" r="47" pathLength="100" />
        <circle className="health-ring__progress" cx="50" cy="50" r="47" pathLength="100" strokeDasharray={`${score} ${100 - score}`} />
      </svg>
      <div><strong>{score}</strong><span>/100</span></div>
    </div>
  )
}

type MobileScene = 'health' | 'issue' | 'fix' | 'verified'

function MobileStatusBar() {
  return (
    <div className="mobile-status-bar" aria-hidden="true">
      <span>9:41</span>
      <div><i /><i /><b /></div>
    </div>
  )
}

function PhoneFrame({ children, className = '', label }: { children: ReactNode; className?: string; label: string }) {
  return (
    <div className={`phone-device ${className}`} aria-label={label}>
      <i className="phone-device__button phone-device__button--top" aria-hidden="true" />
      <i className="phone-device__button phone-device__button--middle" aria-hidden="true" />
      <i className="phone-device__button phone-device__button--side" aria-hidden="true" />
      <div className="phone-device__glass">
        <span className="phone-device__island" aria-hidden="true" />
        {children}
      </div>
    </div>
  )
}

function MobileAppHeader({ title, eyebrow = 'SIDEWISP' }: { title: string; eyebrow?: string }) {
  const { t } = useTranslation()
  return (
    <header className="mobile-app-header">
      <span className="mobile-app-header__mark"><BrandMark compact /></span>
      <span><small>{eyebrow}</small><strong>{title}</strong></span>
      <button type="button" aria-label={`${t('accessibility.notifications')}: 2`}>2</button>
    </header>
  )
}

function MobileBottomNav({ active = 'home' }: { active?: 'home' | 'agents' | 'alerts' | 'you' }) {
  const { t } = useTranslation()
  return (
    <nav className="mobile-bottom-nav" aria-label={t('accessibility.mobileNav')}>
      {(['home', 'agents', 'alerts', 'you'] as const).map((item) => (
        <span key={item} className={item === active ? 'is-active' : ''}><i />{t(`mobile.nav.${item}`)}</span>
      ))}
    </nav>
  )
}

function MobileHealthScreen({ compact = false, checkStatus = 'idle', onRunCheck, onOpenIssue }: {
  compact?: boolean
  checkStatus?: 'idle' | 'running' | 'complete'
  onRunCheck?: () => void
  onOpenIssue?: () => void
}) {
  const { t } = useTranslation()
  const issues = getIssues(t)
  const checkLabel = checkStatus === 'running' ? t('mobile.check.running') : checkStatus === 'complete' ? t('mobile.check.complete') : t('mobile.check.idle')
  const issueSignals = [
    { issue: issues[0], value: '8', unit: t('mobile.status.units.runs') },
    { issue: issues[1], value: '12×', unit: t('mobile.status.units.retries') },
    { issue: issues[2], value: '3', unit: t('mobile.status.units.fails') },
    { issue: issues[3], value: '47m', unit: t('mobile.status.units.late') },
  ]
  const visibleIssues = compact ? issueSignals.slice(0, 3) : issueSignals

  return (
    <div className={`mobile-app-screen mobile-app-screen--health${compact ? ' is-compact' : ''}`}>
      <MobileStatusBar />
      <MobileAppHeader title={t('mobile.status.title')} />
      <div className="mobile-app-content mobile-app-content--health">
        <div className="mobile-health-heading">
          <div><span>{t('mobile.status.eyebrow')}</span><strong className="mobile-health-heading__title">{t('mobile.status.needsAttention')}</strong></div>
          <p><i />{checkLabel}</p>
        </div>
        <section className="mobile-status-hero">
          <div className="mobile-status-hero__top">
            <MobileSeverityIcon severity="critical" />
            <div><span>{t('mobile.status.fleet')}</span><strong>{t('mobile.status.criticalIssues')}</strong><small>{t('mobile.status.blocked')}</small></div>
            <button type="button" onClick={onRunCheck} disabled={!onRunCheck || checkStatus === 'running'}>{checkStatus === 'running' ? t('mobile.check.runningButton') : t('mobile.check.run')}</button>
          </div>
          <div className="mobile-status-metrics">
            <div className="is-critical"><strong>2</strong><span>{t('shared.critical')}</span></div>
            <div className="is-warning"><strong>2</strong><span>{t('shared.warning')}</span></div>
            <div className="is-healthy"><strong>31</strong><span>{t('shared.healthy')}</span></div>
          </div>
        </section>
        <div className="mobile-section-label"><span>{t('mobile.status.topIssues')}</span><small>{t('mobile.status.priority')}</small></div>
        <div className="mobile-issue-list">
          {visibleIssues.map(({ issue, value, unit }, index) => (
            <button
              className={`mobile-issue-row is-${issue.severity}`}
              type="button"
              key={issue.id}
              onClick={index === 0 ? onOpenIssue : undefined}
              disabled={!onOpenIssue || index !== 0}
            >
              <MobileSeverityIcon severity={issue.severity} />
              <span><strong>{issue.title}</strong><small>{issue.agent.split(' / ')[1]} · {issue.category}</small></span>
              <b><strong>{value}</strong><small>{unit}</small></b>
              <ArrowIcon />
            </button>
          ))}
        </div>
        {compact && <div className="mobile-more-issues"><span>{t('mobile.status.more')}</span><strong>{t('mobile.status.viewAll')}</strong></div>}
      </div>
      <MobileBottomNav />
    </div>
  )
}

function MobileIssueScreen({ issue, onNext }: { issue: AgentIssue; onNext?: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="mobile-app-screen mobile-app-screen--detail">
      <MobileStatusBar />
      <div className="mobile-detail-bar"><span>‹</span><strong>{t('mobile.issue.details')}</strong><small>•••</small></div>
      <div className="mobile-detail-content mobile-detail-content--issue">
        <section className={`mobile-issue-hero is-${issue.severity}`}>
          <div><MobileSeverityIcon severity={issue.severity} /><span>{t('mobile.issue.issue', { severity: t(`shared.${issue.severity}`) })}</span><b>{t('shared.open')}</b></div>
          <h3>{issue.title}</h3>
          <p>{issue.impact}</p>
          <footer><span>{issue.agent}</span><span>{t('shared.detected', { time: issue.detected })}</span></footer>
        </section>
        <div className="mobile-confidence"><span>{t('mobile.issue.confidence')}</span><strong>{issue.confidence}% · {t('shared.high')}</strong><progress value={issue.confidence} max="100" aria-label={`${t('mobile.issue.confidence')}: ${issue.confidence}%`} /></div>
        <section className="mobile-evidence">
          <header><span>{t('mobile.issue.why')}</span><small>{t('mobile.issue.signals')}</small></header>
          {issue.evidence.map((item, index) => <div key={item}><i>{index + 1}</i><span>{item}</span></div>)}
        </section>
        <section className="mobile-recommendation"><span>{t('mobile.issue.next')}</span><p>{issue.fix}</p><small>{t('shared.approvalRequired')}</small></section>
      </div>
      <div className="mobile-sticky-action"><button type="button" onClick={onNext}>{t('mobile.issue.review')} <ArrowIcon /></button><small>{t('mobile.issue.withoutYou')}</small></div>
    </div>
  )
}

function MobileFixScreen({ issue, onNext }: { issue: AgentIssue; onNext?: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="mobile-app-screen mobile-app-screen--detail">
      <MobileStatusBar />
      <div className="mobile-detail-bar"><span>‹</span><strong>{t('mobile.fix.review')}</strong><small>•••</small></div>
      <div className="mobile-detail-content mobile-detail-content--fix">
        <div className="mobile-prepared-pill"><i /> {t('mobile.fix.ready')}</div>
        <h3>{t('mobile.fix.title')}</h3>
        <p>{t('mobile.fix.intro')}</p>
        <ol className="mobile-fix-steps">{issue.fixSteps.map((step) => <li key={step}>{step}</li>)}</ol>
        <section className="mobile-boundary-card"><span>{t('mobile.fix.decision')}</span><p>{issue.boundary}</p></section>
        <div className="mobile-fix-facts"><span><i /> {t('mobile.fix.agent')}</span><span><i /> {t('mobile.fix.time')}</span><span><i /> {t('mobile.fix.reversible')}</span></div>
      </div>
      <div className="mobile-sticky-action"><button type="button" onClick={onNext}>{t('mobile.fix.approve')} <ArrowIcon /></button><small>{t('mobile.fix.previewOnly')}</small></div>
    </div>
  )
}

function MobileVerifiedScreen() {
  const { t } = useTranslation()
  return (
    <div className="mobile-app-screen mobile-app-screen--verified">
      <MobileStatusBar />
      <MobileAppHeader title={t('mobile.verified.title')} eyebrow={t('mobile.verified.eyebrow')} />
      <div className="mobile-verified-content">
        <div className="mobile-verified-mark">✓</div>
        <span>{t('mobile.verified.complete')}</span>
        <h3>{t('mobile.verified.heading')}</h3>
        <p>{t('mobile.verified.intro')}</p>
        <section><HealthRing score={96} small /><div><span>{t('mobile.verified.health')}</span><strong>{t('mobile.verified.track')}</strong><small>{t('mobile.verified.justNow')}</small></div></section>
        <ol>
          <li><i />{t('mobile.verified.write')} <small>9:41</small></li>
          <li><i />{t('mobile.verified.persisted')} <small>9:41</small></li>
          <li><i />{t('mobile.verified.progress')} <small>9:42</small></li>
        </ol>
      </div>
      <MobileBottomNav />
    </div>
  )
}

function MobileProductDemo() {
  const { t } = useTranslation()
  const [scene, setScene] = useState<MobileScene>('health')
  const [checkStatus, setCheckStatus] = useState<'idle' | 'running' | 'complete'>('idle')
  const checkTimerRef = useRef<number | null>(null)
  const activeIssue = getIssues(t)[0]

  useEffect(() => () => {
    if (checkTimerRef.current) window.clearTimeout(checkTimerRef.current)
  }, [])

  function runCheck() {
    if (checkStatus === 'running') return
    setCheckStatus('running')
    checkTimerRef.current = window.setTimeout(() => {
      setCheckStatus('complete')
      setScene('verified')
      checkTimerRef.current = null
    }, 850)
  }

  const labels: Record<MobileScene, { eyebrow: string; title: string; text: string }> = {
    health: { eyebrow: t('mobile.demo.detect.eyebrow'), title: t('mobile.demo.detect.title'), text: t('mobile.demo.detect.text') },
    issue: { eyebrow: t('mobile.demo.explain.eyebrow'), title: t('mobile.demo.explain.title'), text: t('mobile.demo.explain.text') },
    fix: { eyebrow: t('mobile.demo.approve.eyebrow'), title: t('mobile.demo.approve.title'), text: t('mobile.demo.approve.text') },
    verified: { eyebrow: t('mobile.demo.verify.eyebrow'), title: t('mobile.demo.verify.title'), text: t('mobile.demo.verify.text') },
  }

  return (
    <div className="mobile-product-demo">
      <div className="mobile-demo-rail" aria-label={t('accessibility.demoViews')}>
        {(Object.keys(labels) as MobileScene[]).map((item) => (
          <button type="button" key={item} className={scene === item ? 'is-active' : ''} aria-pressed={scene === item} onClick={() => setScene(item)}>
            <span>{labels[item].eyebrow}</span><strong>{labels[item].title}</strong><small>{labels[item].text}</small>
          </button>
        ))}
      </div>
      <div className="device-stage device-stage--dark">
        <img src="/product/mobile-signal-field-v1.webp" width="1586" height="992" loading="lazy" decoding="async" alt={t('seo.imageAlt')} aria-hidden="true" />
        <PhoneFrame className="phone-device--product" label={t('accessibility.healthPreview')}>
          {scene === 'health' && <MobileHealthScreen checkStatus={checkStatus} onRunCheck={runCheck} onOpenIssue={() => setScene('issue')} />}
          {scene === 'issue' && <MobileIssueScreen issue={activeIssue} onNext={() => setScene('fix')} />}
          {scene === 'fix' && <MobileFixScreen issue={activeIssue} onNext={() => setScene('verified')} />}
          {scene === 'verified' && <MobileVerifiedScreen />}
        </PhoneFrame>
        <div className="device-stage__badge"><i /> {t('mobile.demo.badge')}</div>
      </div>
    </div>
  )
}

function HeroMobilePreview() {
  const { t } = useTranslation()
  return (
    <div className="hero-mobile-stage">
      <PhoneFrame className="phone-device--hero" label={t('accessibility.heroPreview')}>
        <MobileHealthScreen compact />
      </PhoneFrame>
      <div className="hero-mobile-alert"><span><i /> {t('mobile.hero.alert')}</span><strong>{t('mobile.hero.needsYou')}</strong><small>{t('mobile.hero.memory')}</small></div>
      <aside className="hero-mobile-note" aria-label={t('accessibility.mobileOverview')}>
        <span>{t('mobile.hero.calm')}</span>
        <strong>{t('mobile.hero.heading')}</strong>
        <ul>
          <li><i />{t('mobile.hero.signal')}</li>
          <li><i />{t('mobile.hero.impact')}</li>
          <li><i />{t('mobile.hero.action')}</li>
        </ul>
      </aside>
    </div>
  )
}

function RecoveryPhonePreview() {
  const { t } = useTranslation()
  const recoveryStates = getRecoveryStates(t)
  return (
    <div className="light-device-stage">
      <img src="/product/mobile-signal-field-light-v1.webp" width="1586" height="992" loading="lazy" decoding="async" alt={t('seo.imageAlt')} aria-hidden="true" />
      <PhoneFrame className="phone-device--light-scene" label={t('accessibility.recoveryPreview')}>
        <div className="mobile-app-screen mobile-app-screen--recovery">
          <MobileStatusBar />
          <div className="mobile-detail-bar"><span>‹</span><strong>{t('mobile.recovery.before')}</strong><small>{t('shared.live')}</small></div>
          <div className="mobile-recovery-content">
            <span className="mobile-recovery-kicker">{t('mobile.recovery.kicker')}</span>
            <h3>{t('mobile.recovery.title')}</h3>
            <p>{t('mobile.recovery.intro')}</p>
            <div className="mobile-recovery-states">
              {recoveryStates.map((state) => <div key={state.key} className={state.key === 'stuck' ? 'is-active' : ''}><SeverityDot severity={state.status} /><span><strong>{state.name}</strong><small>{state.detail}</small></span><b>{state.key === 'stuck' ? t('mobile.recovery.match') : t('mobile.recovery.no')}</b></div>)}
            </div>
            <section><span>{t('mobile.recovery.safest')}</span><strong>{t('mobile.recovery.prepare')}</strong><small>{t('shared.approvalRequired')}</small></section>
            <button type="button">{t('mobile.recovery.review')} <ArrowIcon /></button>
          </div>
        </div>
      </PhoneFrame>
    </div>
  )
}

function ConnectionFlowPreview() {
  const { t } = useTranslation()
  return (
    <div className="connection-flow" aria-label={t('accessibility.connectionFlow')}>
      <header>
        <span><i /> {t('connection.stay')}</span>
        <strong>{t('connection.heading')}</strong>
      </header>
      <div className="connection-flow__body">
        <section className="connection-runtimes">
          <span>{t('connection.agents')}</span>
          <div>
            {builtForOptions.map((runtime) => (
              <article key={runtime}>
                <i><IntegrationIcon name={runtime} /></i>
                <strong>{runtime}</strong>
              </article>
            ))}
          </div>
        </section>
        <div className="connection-arrow" aria-hidden="true"><span>{t('connection.matters')}</span><ArrowIcon /></div>
        <section className="connection-adapter">
          <span>{t('connection.sidewisp')}</span>
          <BrandMark />
          <h3>{t('connection.see')}<br />{t('connection.control')}</h3>
          <ul>
            <li><i />{t('connection.sameSetup')}</li>
            <li><i />{t('connection.secrets')}</li>
            <li><i />{t('connection.approve')}</li>
          </ul>
        </section>
        <div className="connection-arrow" aria-hidden="true"><span>{t('connection.answer')}</span><ArrowIcon /></div>
        <section className="connection-report">
          <span>{t('connection.needsYou')}</span>
          <div><BrandMark compact /><small>SIDEWISP</small></div>
          <strong>Agent Jarvis<br />{t('connection.attention')}</strong>
          <p>{t('connection.explanation')}</p>
          <footer><i /> {t('shared.ready')}</footer>
        </section>
      </div>
      <footer><span>{t('connection.agentsFooter')}</span><span>{t('connection.dataFooter')}</span><span>{t('connection.decisionFooter')}</span></footer>
    </div>
  )
}

function AuthorityPhonePreview() {
  const { t } = useTranslation()
  return (
    <div className="authority-mobile-stage">
      <img src="/product/mobile-signal-field-v1.webp" width="1586" height="992" loading="lazy" decoding="async" alt={t('seo.imageAlt')} aria-hidden="true" />
      <PhoneFrame className="phone-device--authority" label={t('accessibility.authorityPreview')}>
        <div className="mobile-app-screen mobile-app-screen--authority">
          <MobileStatusBar />
          <MobileAppHeader title={t('mobile.authority.title')} eyebrow={t('mobile.authority.eyebrow')} />
          <div className="mobile-authority-content">
            <div className="mobile-authority-icon">!</div>
            <span>{t('mobile.authority.nothingRun')}</span>
            <h3>{t('mobile.authority.question')}</h3>
            <p>{t('mobile.authority.intro')}</p>
            <section>
              <div><span>{t('mobile.authority.scope')}</span><strong>{t('mobile.authority.agent')}</strong></div>
              <div><span>{t('mobile.authority.timeLimit')}</span><strong>{t('mobile.authority.minutes')}</strong></div>
              <div><span>{t('mobile.authority.rollback')}</span><strong>{t('mobile.authority.available')}</strong></div>
            </section>
            <button type="button">{t('mobile.authority.approve')}</button>
            <button type="button">{t('mobile.authority.review')}</button>
          </div>
        </div>
      </PhoneFrame>
    </div>
  )
}

function PlainEnglishSection() {
  const { t } = useTranslation()
  const [activeMode, setActiveMode] = useState<DescriptionMode>('simple')
  const projectDescriptions = getProjectDescriptions(t)
  const activeDescription = projectDescriptions.find((description) => description.mode === activeMode) ?? projectDescriptions[0]

  return (
    <section className="plain-section" aria-labelledby="plain-title">
      <div className="plain-section__intro" data-reveal>
        <div className="section-index section-index--dark"><span>{t('descriptions.about')}</span><span>{t('descriptions.plain')}</span></div>
        <div>
          <h2 id="plain-title"><span>{t('descriptions.title')}</span></h2>
        </div>
      </div>

      <div className={`plain-console plain-console--${activeMode}`}>
        <div className="plain-console__rail" role="tablist" aria-label={t('accessibility.descriptions')}>
          <div className="plain-console__rail-head">
            <span>{t('descriptions.choose')}</span>
            <i aria-hidden="true" />
          </div>
          {projectDescriptions.map((description) => (
            <button
              className={description.mode === activeMode ? 'is-active' : ''}
              type="button"
              role="tab"
              aria-selected={description.mode === activeMode}
              aria-controls="plain-description-panel"
              key={description.mode}
              onClick={() => setActiveMode(description.mode)}
            >
              <span>{description.index}</span>
              <strong>{description.label}</strong>
              <small>{description.hint}</small>
              <i aria-hidden="true">→</i>
            </button>
          ))}
          <div className="plain-console__promise">
            <BrandMark compact />
            <span><small>{t('descriptions.point')}</small><strong>{t('descriptions.promise')}</strong></span>
          </div>
        </div>

        <div className="plain-console__stage" id="plain-description-panel" role="tabpanel">
          <header>
            <span><i /> {activeDescription.label}</span>
            <small>{t('descriptions.project')}</small>
          </header>
          <div className="plain-console__copy" aria-live="polite">
            <span>{activeDescription.index}</span>
            <p key={activeDescription.mode}>{activeDescription.copy}</p>
          </div>
          <footer>
            <span>{t('descriptions.health')}</span>
            <span>{t('descriptions.issues')}</span>
            <span>{t('descriptions.attention')}</span>
          </footer>
        </div>
      </div>
    </section>
  )
}

function SiteApp() {
  const { t } = useTranslation()
  const shellRef = useRef<HTMLDivElement>(null)
  const healthChecks = getHealthChecks(t)
  const productFlow = getProductFlow(t)

  useEffect(() => {
    if (window.location.pathname === '/' && !localeFromPath()) {
      let preferredLocale: Locale | null = null
      try {
        const storedLocale = window.localStorage.getItem('sidewisp-locale')?.toLowerCase()
        if (isLocale(storedLocale)) preferredLocale = storedLocale
      } catch {
        // Fall through to the browser preference when storage is unavailable.
      }
      preferredLocale ??= navigator.languages
        .map((language) => language.split('-')[0]?.toLowerCase())
        .find(isLocale) ?? null

      if (preferredLocale && preferredLocale !== 'en') {
        window.history.replaceState({ locale: preferredLocale }, '', localizedPath(preferredLocale))
        void i18n.changeLanguage(preferredLocale)
      }
    }

    const syncLocaleFromUrl = () => {
      const urlLocale = localeFromPath() ?? 'en'
      if (urlLocale !== currentLocale()) void i18n.changeLanguage(urlLocale).then(() => trackPageView())
      else trackPageView()
    }
    window.addEventListener('popstate', syncLocaleFromUrl)
    return () => window.removeEventListener('popstate', syncLocaleFromUrl)
  }, [])

  useEffect(() => {
    const locale = currentLocale()
    if (!localeFromPath() && locale !== 'en' && window.location.pathname === '/') {
      window.history.replaceState({ locale }, '', localizedPath(locale))
    }

    try {
      if (window.localStorage.getItem('sidewisp-locale') !== locale) {
        window.localStorage.setItem('sidewisp-locale', locale)
      }
    } catch {
      // Language detection still works when storage is unavailable.
    }

    document.documentElement.dataset.locale = locale
    syncDocumentSeo(locale, t)
  }, [t])

  useEffect(() => {
    const shell = shellRef.current
    if (!shell) return
    const revealObserver = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('is-visible')
      }),
      { threshold: 0.1 },
    )
    shell.querySelectorAll<HTMLElement>('[data-reveal]').forEach((element) => revealObserver.observe(element))

    const onScroll = () => {
      shell.style.setProperty('--scroll-progress', `${Math.min(window.scrollY / Math.max(1, document.body.scrollHeight - window.innerHeight), 1)}`)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => {
      revealObserver.disconnect()
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  return (
    <div className="site-shell" ref={shellRef}>
      <a className="skip-link" href="#main-content">{t('accessibility.skip')}</a>
      <div className="scroll-progress" aria-hidden="true" />

      <PublicHeader />

      <main id="main-content">
        <section className="hero" id="top" data-beasties-container>
          <div className="hero-grid" aria-hidden="true" />
          <div className="hero-atmosphere" aria-hidden="true" />
          <div className="hero-copy" data-reveal>
            <div className="hero-eyebrow"><span><i /> {t('hero.eyebrow')}</span></div>
            <h1><span>{t('hero.line1')}</span><strong>{t('hero.line2')}</strong></h1>
            <p>{t('hero.intro')}</p>
            <WaitlistForm surface="hero" />
          </div>
          <div className="hero-visual" data-reveal><HeroMobilePreview /></div>
          <div className="hero-foot">
            <div><span>{t('hero.firstReport')}</span><strong>{t('hero.inMinutes')}</strong></div>
            <div><span>{t('hero.noNew')}</span><strong>{t('hero.runtime')}</strong></div>
            <div><span>{t('hero.humans')}</span><strong>{t('hero.authority')}</strong></div>
            <a href="#product">{t('hero.how')} <i /></a>
          </div>
        </section>

        <PlainEnglishSection />

        <section className="silent-section">
          <div className="section-heading silent-heading" data-reveal>
            <div className="section-index"><span>01</span><span>{t('silent.index')}</span></div>
            <h2><span>{t('silent.line1')}</span><strong>{t('silent.line2')}</strong></h2>
            <p>{t('silent.intro')}</p>
          </div>

          <div className="failure-compare" data-reveal>
            <article className="failure-pane failure-pane--agent">
              <header className="failure-pane__header">
                <span>{t('silent.agentReports')}</span>
                <strong><i /> {t('silent.complete')}</strong>
              </header>

              <div className="agent-report">
                <div className="agent-report__identity">
                  <BrandMark compact />
                  <span><small>OPENCLAW</small><strong>Agent Jarvis</strong></span>
                  <time>10:42:18</time>
                </div>
                <p>{t('silent.quote')}</p>
                <div className="agent-report__signals">
                  <div><span>{t('silent.runs')}</span><strong>8 / 8</strong><i /></div>
                  <div><span>{t('silent.toolCalls')}</span><strong>14 OK</strong><i /></div>
                  <div><span>{t('silent.finalStatus')}</span><strong>{t('silent.success')}</strong><i /></div>
                </div>
              </div>

              <div className="missed-signal">
                <span><i>!</i> {t('silent.absent')}</span>
                <div><small>{t('silent.writes')}</small><strong>0 / 8</strong></div>
                <p>{t('silent.missingImpact')}</p>
              </div>

              <div className="failure-mobile-summary">
                <div><BrandMark compact /><span><small>AGENT JARVIS</small><strong>{t('silent.reported')}</strong></span><b><i /> {t('silent.complete')}</b></div>
                <p>{t('silent.completed')}</p>
                <span><i>!</i> {t('silent.stayed')} <strong>0 / 8</strong></span>
              </div>
            </article>

            <div className="failure-reveal" aria-hidden="true">
              <span>{t('silent.reveal')}</span>
              <ArrowIcon />
            </div>

            <article className="failure-pane failure-pane--sidewisp">
              <header className="failure-pane__header">
                <span><BrandMark compact /> {t('silent.detected')}</span>
                <time>{t('silent.ago')}</time>
              </header>

              <div className="visible-incident">
                <div className="visible-incident__severity"><i>!</i><span>{t('silent.critical')}</span></div>
                <h3>{t('silent.title')}</h3>
                <p>{t('silent.impact')}</p>
              </div>

              <div className="incident-proof">
                <div><small>{t('silent.decisions')}</small><strong>{t('silent.runsValue')}</strong><span>{t('silent.forgotten')}</span></div>
                <div><small>{t('silent.rework')}</small><strong>{t('silent.next')}</strong><span>{t('silent.restart')}</span></div>
                <div><small>{t('silent.confidence')}</small><strong>96%</strong><span>{t('silent.evidence')}</span></div>
              </div>

              <div className="incident-action">
                <span>{t('silent.action')}</span>
                <strong>{t('silent.fix')}</strong>
                <small>{t('silent.approval')} <i>→</i></small>
              </div>
            </article>
          </div>

        </section>

        <section className="product-section" id="product">
          <div className="product-intro" data-reveal>
            <div className="section-index section-index--dark"><span>02</span><span>{t('product.index')}</span></div>
            <h2><span>{t('product.line1')}</span><strong>{t('product.line2')}</strong></h2>
            <p>{t('product.intro')}</p>
          </div>
          <div data-reveal><MobileProductDemo /></div>
        </section>

        <section className="flow-section">
          <div className="flow-intro" data-reveal>
            <div className="section-index"><span>03</span><span>{t('flow.index')}</span></div>
            <h2><span>{t('flow.line1')}</span><strong>{t('flow.line2')}</strong></h2>
            <div className="flow-intro__aside">
              <p>{t('flow.intro')}</p>
              <div><span><i /> {t('flow.checks')}</span><strong>{t('flow.promise')}</strong></div>
            </div>
          </div>

          <div className="flow-console" data-reveal>
            <header className="flow-console__header">
              <div><BrandMark compact /><span><small>{t('flow.live')}</small><strong>{t('flow.incident')}</strong></span></div>
              <div className="flow-console__status"><i /><span>{t('flow.monitoring')}</span><time>{t('flow.updated')}</time></div>
            </header>

            <div className="flow-console__summary">
              <div><span>{t('flow.wrong')}</span><strong>{t('flow.wrongValue')}</strong></div>
              <div><span>{t('flow.status')}</span><strong>{t('flow.detected')}</strong></div>
              <div><span>{t('flow.next')}</span><strong>{t('flow.waits')}</strong></div>
            </div>

            <div className="flow-track">
              {productFlow.map((step, index) => (
                <article className={`flow-step flow-step--${step.state}`} key={step.title}>
                  <div className="flow-step__top">
                    <span>{step.index}</span>
                    <i>{index < productFlow.length - 1 ? '→' : '✓'}</i>
                  </div>
                  <small>{step.label}</small>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                  <div className="flow-step__proof">
                    <strong>{step.metric}</strong>
                    <span>{step.metricLabel}</span>
                    <small>{step.detail}</small>
                  </div>
                </article>
              ))}
            </div>

            <footer className="flow-console__footer">
              <div className="flow-loopback"><i>↻</i><span><small>{t('flow.noFalse')}</small><strong>{t('flow.healthy')}</strong></span></div>
              <div className="flow-principles"><span>{t('flow.proven')}</span><span>{t('flow.approved')}</span><span>{t('flow.confirmed')}</span></div>
            </footer>
          </div>
        </section>

        <section className="checks-section" id="checks">
          <div className="checks-intro" data-reveal>
            <div className="section-index section-index--dark"><span>04</span><span>{t('checks.index')}</span></div>
            <h2><span>{t('checks.line1')}</span><strong>{t('checks.line2')}</strong></h2>
            <p>{t('checks.intro')}</p>
          </div>
          <div className="checks-showcase" data-reveal>
            <article className="health-report">
              <header className="health-report__header">
                <div><BrandMark compact /><span><small>{t('checks.example')}</small><strong>{t('checks.review')}</strong></span></div>
                <time>{t('checks.data')}</time>
              </header>
              <div className="health-report__summary">
                <div className="health-score"><strong>71</strong><span>/ 100</span><small>{t('checks.attention')}</small></div>
                <div className="health-verdict">
                  <span><i /> {t('checks.mismatch')}</span>
                  <h3>{t('checks.finished')}<br />{t('checks.notWork')}</h3>
                  <p>{t('checks.reportMissing')}</p>
                </div>
              </div>
              <div className="health-evidence">
                <div><span>{t('checks.runStatus')}</span><strong className="is-ok">{t('checks.complete')}</strong><small>10:42:18</small></div>
                <div><span>{t('checks.expected')}</span><strong>weekly-report.md</strong><small>{t('checks.required')}</small></div>
                <div className="is-critical"><span>{t('checks.observed')}</span><strong>{t('checks.notFound')}</strong><small>{t('checks.scan')}</small></div>
              </div>
              <footer className="health-report__footer">
                <div><span>{t('checks.confidence')}</span><strong>{t('checks.high')}</strong></div>
                <div><span>{t('checks.impact')}</span><strong>{t('checks.deliverable')}</strong></div>
                <div><span>{t('checks.action')}</span><strong>{t('checks.actionValue')}</strong></div>
              </footer>
            </article>

            <aside className="signal-stack" aria-label={t('accessibility.signals')}>
              <div className="signal-stack__head"><span>{t('checks.coverage')}</span><strong>{t('checks.signals')}</strong></div>
              {healthChecks.map((check, index) => {
                const state = index === 4 ? 'critical' : index === 2 || index === 5 ? 'warning' : 'healthy'
                const result = index === 4 ? t('checks.missing') : index === 2 ? t('checks.context') : index === 5 ? t('checks.cost') : t('shared.noIssue')
                return (
                  <article className={`signal-row is-${state}`} key={check.title}>
                    <span className="signal-row__index">{check.index}</span>
                    <span><strong>{check.title}</strong><small>{check.description}</small></span>
                    <b><i />{result}</b>
                  </article>
                )
              })}
              <div className="signal-stack__foot"><span>{t('checks.retained')}</span><span>{t('checks.freshness')}</span><span>{t('checks.human')}</span></div>
            </aside>
          </div>
        </section>

        <section className="recovery-section" id="recovery">
          <div className="recovery-copy recovery-copy--wide" data-reveal>
            <div className="section-index"><span>05</span><span>{t('recovery.index')}</span></div>
            <h2><span>{t('recovery.line1')}</span><strong>{t('recovery.line2')}</strong></h2>
            <p>{t('recovery.intro')}</p>
            <div className="recovery-boundary"><BrandMark compact /><span><strong>{t('recovery.finalSay')}</strong><small>{t('recovery.authority')}</small></span></div>
          </div>
          <div className="recovery-previews" data-reveal>
            <article className="recovery-preview-card">
              <header><span>{t('recovery.diagnose')}</span><strong>{t('recovery.state')}</strong></header>
              <RecoveryPhonePreview />
            </article>
            <article className="recovery-preview-card recovery-preview-card--approval">
              <header><span>{t('recovery.approve')}</span><strong>{t('recovery.withoutYou')}</strong></header>
              <AuthorityPhonePreview />
            </article>
          </div>
        </section>

        <section className="integrations-section" id="integrations">
          <div className="integrations-copy" data-reveal>
            <div className="section-index"><span>06</span><span>{t('integrations.index')}</span></div>
            <h2><span>{t('integrations.line1')}</span><strong>{t('integrations.line2')}</strong></h2>
            <p>{t('integrations.intro')}</p>
            <div className="connection-promises">
              <span><i /> {t('integrations.setup')}</span>
              <span><i /> {t('integrations.secrets')}</span>
              <span><i /> {t('integrations.decisions')}</span>
            </div>
          </div>
          <div data-reveal><ConnectionFlowPreview /></div>
          <article className="open-source-proof" aria-labelledby="open-source-title" data-reveal>
            <div className="open-source-proof__topline">
              <span><i /> {t('integrations.openSource.eyebrow')}</span>
              <div aria-hidden="true"><span>MIT</span><span>v0.2.0</span><span>&lt;/&gt;</span></div>
            </div>
            <div className="open-source-proof__intro">
              <h3 id="open-source-title">{t('integrations.openSource.title')}</h3>
              <p>{t('integrations.openSource.intro')}</p>
              <div className="open-source-proof__links">
                <a href="https://github.com/golem-workers/sidewisp-plugin/tree/v0.2.0" target="_blank" rel="noreferrer">
                  {t('integrations.openSource.sourceCta')} <span aria-hidden="true">↗</span>
                </a>
                <a href="/docs/#build">
                  {t('integrations.openSource.buildCta')} <ArrowIcon />
                </a>
              </div>
            </div>
            <div className="open-source-proof__artifact" aria-hidden="true">
              <header>
                <span><i /><i /><i /></span>
                <code>sidewisp-plugin / sanitize.js</code>
                <b>MIT</b>
              </header>
              <div className="open-source-proof__code">
                <span><em>01</em><code><b>export</b> function sanitizeTelemetryEvent(input) {'{'}</code></span>
                <span><em>02</em><code>&nbsp;&nbsp;<b>return</b> {'{'}</code></span>
                <span><em>03</em><code>&nbsp;&nbsp;&nbsp;&nbsp;schema: <i>"sidewisp.telemetry.v1"</i>,</code></span>
                <span><em>04</em><code>&nbsp;&nbsp;&nbsp;&nbsp;runtime, source, type, outcome,</code></span>
                <span><em>05</em><code>&nbsp;&nbsp;{'}'}</code></span>
                <span><em>06</em><code>{'}'}</code></span>
              </div>
              <div className="open-source-proof__pipeline">
                <span><i>&lt;/&gt;</i><code>source.js</code></span>
                <b>→</b>
                <span><i>{'{ }'}</i><code>schema.v1</code></span>
                <b>→</b>
                <span><i>◫</i><code>dashboard/*</code></span>
              </div>
            </div>
            <div className="open-source-proof__checks">
              <section>
                <header><span>01</span><i aria-hidden="true">&lt;/&gt;</i></header>
                <h4>{t('integrations.openSource.auditTitle')}</h4>
                <p>{t('integrations.openSource.auditText')}</p>
              </section>
              <section>
                <header><span>02</span><i aria-hidden="true">{'{ }'}</i></header>
                <h4>{t('integrations.openSource.dataTitle')}</h4>
                <p>{t('integrations.openSource.dataText')}</p>
                <a href="/docs/#telemetry">{t('integrations.openSource.contractCta')} <span aria-hidden="true">→</span></a>
              </section>
              <section>
                <header><span>03</span><i aria-hidden="true">◫</i></header>
                <h4>{t('integrations.openSource.ownTitle')}</h4>
                <p>{t('integrations.openSource.ownText')}</p>
              </section>
            </div>
            <footer>
              <span>{t('integrations.openSource.license')}</span>
              <span>{t('integrations.openSource.noLockIn')}</span>
            </footer>
          </article>
        </section>

        <section className="final-cta" id="waitlist">
          <div className="final-cta__topline"><Logo /><span>{t('final.preview')}</span></div>
          <div className="final-cta__copy" data-reveal>
            <span>{t('final.eyebrow')}</span>
            <h2><span>{t('final.line1')}</span><strong>{t('final.line2')}</strong></h2>
            <p>{t('final.intro')}</p>
            <div className="final-cta__fit" aria-label={t('accessibility.previewFocus')}>
              <span><b>01</b>{t('final.solo')}</span>
              <span><b>02</b>{t('final.developers')}</span>
              <span><b>03</b>{t('final.teams')}</span>
            </div>
            <WaitlistForm surface="footer" />
          </div>
          <PublicFooter />
        </section>
      </main>
    </div>
  )
}

export default function App({ pathname }: { pathname?: string } = {}) {
  const currentPath = pathname ?? (typeof window === 'undefined' ? '/' : window.location.pathname)
  const pathnameOnly = currentPath.split(/[?#]/, 1)[0]
  const normalizedPath = pathnameOnly.replace(/\/+$/, '') || '/'
  if (/^\/app(?:\/|$)/.test(currentPath)) {
    return <Suspense fallback={null}><ProductApp /></Suspense>
  }
  if (/^\/admin(?:\/|$)/.test(currentPath)) {
    return <Suspense fallback={null}><ProductApp /></Suspense>
  }
  if (normalizedPath === '/design') {
    return <Suspense fallback={null}><DesignSystem /></Suspense>
  }
  if (normalizedPath === '/design-app') {
    return <Suspense fallback={null}><DesignApp /></Suspense>
  }
  if (normalizedPath === '/terms' || normalizedPath === '/privacy') {
    return <LegalPage kind={normalizedPath === '/terms' ? 'terms' : 'privacy'} />
  }
  if (normalizedPath === '/docs') {
    return <Suspense fallback={null}><DocsPage /></Suspense>
  }
  const locale = localeFromPath(normalizedPath) ?? 'en'
  const localizedRoute = pathWithoutLocale(normalizedPath).replace(/\/+$/, '') || '/'
  if (localizedRoute === '/articles') {
    return <Suspense fallback={null}><ArticlesPage locale={locale} /></Suspense>
  }
  const articleMatch = /^\/articles\/([^/]+)$/.exec(localizedRoute)
  if (articleMatch) {
    return <Suspense fallback={null}><ArticlePage slug={decodeURIComponent(articleMatch[1])} locale={locale} /></Suspense>
  }
  return <SiteApp />
}
